import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { uploadToCloudinary, cloudinaryConfigured } from '../../utils/cloudinary';
import { getProductDepartments } from '../../utils/departments';

const DEPARTMENTS = [
  'Automotivos', 'Beleza', 'Bolsas', 'Brinquedos', 'Camping/Pesca',
  'Diversos', 'Eletro/Eletrônicos', 'Esportes/Fitness', 'Ferramentas',
  'Informática', 'Materiais e equipamentos para construção', 'Pet', 'Utilidades Domésticas',
];

const EMPTY_FORM = {
  code: '', name: '', price: '', departments: [], stock: '1',
  description: '', images: ['', '', '', '', ''], featured: false,
  weight: '', dimLength: '', dimWidth: '', dimHeight: '',
  supplier: '',
};

export default function NewProduct() {
  const location = useLocation();
  const navigate = useNavigate();
  const editing = location.state?.product;

  const [form, setForm]           = useState(EMPTY_FORM);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState('');
  const [uploadingIndex, setUploadingIndex] = useState(null);

  useEffect(() => {
    api.get('/admin/suppliers').then(res => setSuppliers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '',
        price: editing.price || '',
        departments: getProductDepartments(editing),
        stock: editing.stock ?? '1',
        description: editing.description || '',
        images: [...(editing.images || []), '', '', '', '', ''].slice(0, 5),
        featured: editing.featured || false,
        weight: editing.weight || '',
        dimLength: editing.dimensions?.length || '',
        dimWidth: editing.dimensions?.width || '',
        dimHeight: editing.dimensions?.height || '',
        supplier: editing.supplier?._id || editing.supplier || '',
      });
    }
  }, [editing]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const toggleDepartment = (name) => {
    setForm(prev => {
      const has = prev.departments.includes(name);
      return {
        ...prev,
        departments: has ? prev.departments.filter(d => d !== name) : [...prev.departments, name],
      };
    });
    setSuccess('');
  };

  const handleImageChange = (index) => (e) => {
    const value = e.target.value;
    setForm(prev => {
      const images = [...prev.images];
      images[index] = value;
      return { ...prev, images };
    });
    setSuccess('');
  };

  const handleImageUpload = (index) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIndex(index);
    try {
      const url = await uploadToCloudinary(file);
      setForm(prev => {
        const images = [...prev.images];
        images[index] = url;
        return { ...prev, images };
      });
      setSuccess('');
    } catch (err) {
      alert(err.message || 'Erro ao enviar a imagem.');
    } finally {
      setUploadingIndex(null);
      e.target.value = ''; // permite reenviar o mesmo arquivo
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.departments.length === 0) {
      alert('Selecione ao menos um departamento.');
      return;
    }

    setLoading(true);
    setSuccess('');

    const body = {
      code: form.code,
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      departments: form.departments,
      stock: parseInt(form.stock, 10),
      featured: form.featured,
      images: form.images.map(s => s.trim()).filter(Boolean).slice(0, 5),
      weight: form.weight ? parseFloat(form.weight) : 0,
      dimensions: {
        length: form.dimLength ? parseFloat(form.dimLength) : 0,
        width: form.dimWidth ? parseFloat(form.dimWidth) : 0,
        height: form.dimHeight ? parseFloat(form.dimHeight) : 0,
      },
      supplier: form.supplier || null,
    };

    try {
      if (editing) {
        await api.put(`/admin/products/${editing._id}`, body);
        setSuccess('Produto atualizado com sucesso!');
        setTimeout(() => navigate('/tocadochefe/painel/produtos'), 1500);
      } else {
        await api.post('/admin/products', body);
        setSuccess('Produto cadastrado com sucesso!');
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      alert('Erro ao salvar produto. Verifique se o servidor está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setForm(EMPTY_FORM);
    setSuccess('');
  };

  return (
    <>
      <h1 className="page-title">{editing ? 'Editar Produto' : 'Novo Produto'}</h1>

      {success && <div className="alert-success">{success}</div>}

      <form onSubmit={handleSubmit} className="admin-form-card">

        <div className="form-grid-2">
          <div className="form-group">
            <label>Código do Produto</label>
            {editing ? (
              <input
                className="form-control-admin"
                value={editing.code || '—'}
                readOnly
                style={{ background: '#f0f0f0', color: '#555', cursor: 'not-allowed', fontWeight: '700', letterSpacing: '2px' }}
              />
            ) : (
              <input
                className="form-control-admin"
                placeholder="Ex: 001"
                value={form.code}
                onChange={handleChange('code')}
                required
              />
            )}
          </div>
          <div className="form-group">
            <label>Nome do Produto</label>
            <input
              className="form-control-admin"
              value={form.name}
              onChange={handleChange('name')}
              required
            />
          </div>
          <div className="form-group">
            <label>Preço</label>
            <input
              className="form-control-admin"
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={handleChange('price')}
              required
            />
          </div>
        </div>

        <div className="form-grid-2">
          <div className="form-group">
            <label>Fornecedor</label>
            <select
              className="form-control-admin"
              value={form.supplier}
              onChange={handleChange('supplier')}
            >
              <option value="">Sem fornecedor</option>
              {suppliers.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Estoque</label>
            <input
              className="form-control-admin"
              type="number"
              min="0"
              value={form.stock}
              onChange={handleChange('stock')}
              required
            />
          </div>
        </div>

        <fieldset style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px 16px', marginBottom: '12px' }}>
          <legend style={{ fontSize: '14px', fontWeight: '600', color: '#555', padding: '0 6px' }}>
            Departamentos (marque um ou mais) *
          </legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '4px 14px' }}>
            {DEPARTMENTS.map(d => (
              <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0, cursor: 'pointer', fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={form.departments.includes(d)}
                  onChange={() => toggleDepartment(d)}
                  style={{ width: 'auto' }}
                />
                {d}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="form-group">
          <label>Descrição</label>
          <textarea
            className="form-control-admin"
            rows={4}
            value={form.description}
            onChange={handleChange('description')}
            style={{ resize: 'vertical' }}
          />
        </div>

        <fieldset style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px 16px', marginBottom: '12px' }}>
          <legend style={{ fontSize: '14px', fontWeight: '600', color: '#555', padding: '0 6px' }}>
            Imagens do produto (até 5 — a 1ª é a principal)
          </legend>
          {!cloudinaryConfigured && (
            <p style={{ fontSize: '12px', color: '#b7791f', marginBottom: 10 }}>
              Envio de arquivos indisponível (Cloudinary não configurado). Você ainda pode colar a URL da imagem.
            </p>
          )}
          {form.images.map((img, i) => (
            <div className="form-group" key={i}>
              <label>{i === 0 ? 'Imagem 1 (principal)' : `Imagem ${i + 1} (opcional)`}</label>
              <input
                className="form-control-admin"
                placeholder={i === 0 ? 'Cole a URL ou envie um arquivo' : 'URL da imagem ou envie um arquivo'}
                value={img}
                onChange={handleImageChange(i)}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                {cloudinaryConfigured && (
                  <label
                    className="btn-admin-secondary"
                    style={{ cursor: uploadingIndex !== null ? 'not-allowed' : 'pointer', margin: 0, fontSize: 13, padding: '5px 12px' }}
                  >
                    {uploadingIndex === i ? 'Enviando...' : '📤 Enviar arquivo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={uploadingIndex !== null}
                      onChange={handleImageUpload(i)}
                    />
                  </label>
                )}
                {img && (
                  <img
                    src={img}
                    alt={`Prévia ${i + 1}`}
                    style={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                  />
                )}
              </div>
            </div>
          ))}
        </fieldset>

        <fieldset style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px 16px', marginBottom: '12px' }}>
          <legend style={{ fontSize: '14px', fontWeight: '600', color: '#555', padding: '0 6px' }}>
            Dimensões e Peso (para cálculo de frete)
          </legend>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Peso (kg)</label>
              <input
                className="form-control-admin"
                type="number"
                step="0.001"
                min="0"
                placeholder="Ex: 1.500"
                value={form.weight}
                onChange={handleChange('weight')}
              />
            </div>
            <div className="form-group">
              <label>Comprimento (cm)</label>
              <input
                className="form-control-admin"
                type="number"
                step="0.1"
                min="0"
                placeholder="Ex: 30"
                value={form.dimLength}
                onChange={handleChange('dimLength')}
              />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Largura (cm)</label>
              <input
                className="form-control-admin"
                type="number"
                step="0.1"
                min="0"
                placeholder="Ex: 20"
                value={form.dimWidth}
                onChange={handleChange('dimWidth')}
              />
            </div>
            <div className="form-group">
              <label>Altura (cm)</label>
              <input
                className="form-control-admin"
                type="number"
                step="0.1"
                min="0"
                placeholder="Ex: 10"
                value={form.dimHeight}
                onChange={handleChange('dimHeight')}
              />
            </div>
          </div>
        </fieldset>

        <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="featured"
            checked={form.featured}
            onChange={handleChange('featured')}
          />
          <label htmlFor="featured" style={{ marginBottom: 0, cursor: 'pointer' }}>
            Destacar na página inicial
          </label>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button type="submit" className="btn-admin-primary" disabled={loading}>
            {loading ? 'Salvando...' : (editing ? 'Atualizar Produto' : 'Cadastrar Produto')}
          </button>
          <button type="button" className="btn-admin-secondary" onClick={handleClear}>
            Limpar
          </button>
        </div>
      </form>
    </>
  );
}
